import { useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import {
    isValidCNPJ,
    isValidCPF,
    isValidEmail,
    maskCNPJ,
    maskCPF,
    maskPhone,
    slugifyShopName,
} from "../lib/format";

const PLANS = [
    { id: "Starter", price: 97, perks: ["Agenda inteligente", "1 profissional"] },
    { id: "Pro", price: 197, perks: ["Multi-profissional", "WhatsApp ilimitado"], recommended: true },
    { id: "Premium", price: 297, perks: ["Domínio próprio", "Modo Kiosk + PIN", "Multi-unidades", "Suporte 24/7"] },
];

/**
 * RegisterModal — Cadastro completo do lojista a partir da Landing Page.
 * Coleta: PF (CPF) ou PJ (CNPJ), nome, e-mail, telefone, nome do salão,
 * identificador único (shopId), plano escolhido e senha.
 * Persiste em SalonContext.registrations (localStorage).
 */
const RegisterModal = ({ open, onClose }) => {
    const { addRegistration } = useSalon();
    const [step, setStep] = useState(1);
    const [kind, setKind] = useState("PJ"); // 'PF' | 'PJ'
    const [doc, setDoc] = useState("");
    const [ownerName, setOwnerName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [shopName, setShopName] = useState("");
    const [shopId, setShopId] = useState("");
    const [plan, setPlan] = useState("Pro");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [kioskPin, setKioskPin] = useState("2580");
    const [financialPin, setFinancialPin] = useState("1234");
    const [loading, setLoading] = useState(false);

    if (!open) return null;

    const validateStep1 = () => {
        if (!ownerName.trim()) return "Informe o nome completo.";
        if (!isValidEmail(email)) return "E-mail inválido.";
        if (phone.replace(/\D/g, "").length < 10) return "Telefone incompleto.";
        if (kind === "PF" && !isValidCPF(doc)) return "CPF deve ter 11 dígitos.";
        if (kind === "PJ" && !isValidCNPJ(doc)) return "CNPJ deve ter 14 dígitos.";
        return null;
    };

    const validateStep2 = () => {
        if (!shopName.trim()) return "Informe o nome do salão.";
        if (shopId.length < 3) return "Identificador da loja precisa ter 3+ caracteres.";
        if (!/^[a-z0-9]+$/.test(shopId)) return "Identificador só pode conter letras minúsculas e números.";
        return null;
    };

    const validateStep3 = () => {
        if (password.length < 8) return "Senha precisa ter 8+ caracteres.";
        if (password !== confirm) return "Senhas não conferem.";
        if (!/^\d{4}$/.test(kioskPin)) return "PIN do Modo Kiosk precisa ter 4 dígitos numéricos.";
        if (!/^\d{4}$/.test(financialPin)) return "PIN do Painel Financeiro precisa ter 4 dígitos numéricos.";
        return null;
    };

    const handleNext = () => {
        const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
        if (err) {
            toast.error(err);
            return;
        }
        setStep((s) => s + 1);
    };

    const handleSubmit = () => {
        const err = validateStep3();
        if (err) {
            toast.error(err);
            return;
        }
        setLoading(true);
        setTimeout(() => {
            addRegistration({
                kind,
                document: doc,
                ownerName: ownerName.trim(),
                email: email.trim().toLowerCase(),
                phone,
                shopName: shopName.trim(),
                shopId: shopId.trim(),
                plan,
                status: "trial",
                mrr: 0,
                kioskPin,
                financialPin,
            });
            toast.success("Cadastro concluído! 🎉", {
                description: `Bem-vindo, ${ownerName.split(" ")[0]}. Trial de 14 dias ativado.`,
            });
            setLoading(false);
            onClose?.();
            // reset
            setStep(1);
            setDoc("");
            setOwnerName("");
            setEmail("");
            setPhone("");
            setShopName("");
            setShopId("");
            setPassword("");
            setConfirm("");
        }, 900);
    };

    return (
        <div
            data-testid="register-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl rounded-3xl border border-white/10 bg-slate-950 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
                {/* glow */}
                <div className="absolute -inset-px rounded-3xl opacity-40 blur-2xl pointer-events-none bg-gradient-to-br from-amber-500 to-amber-900"></div>

                <header className="relative p-6 md:p-8 border-b border-white/5 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400">
                            Cadastro · Etapa {step}/3
                        </div>
                        <h2 className="salon-heading text-2xl text-white mt-1">
                            {step === 1 && "Quem é você?"}
                            {step === 2 && "Sobre o seu salão"}
                            {step === 3 && "Plano e segurança"}
                        </h2>
                    </div>
                    <button
                        data-testid="register-close-btn"
                        onClick={onClose}
                        className="h-10 w-10 rounded-full bg-white/5 hover:bg-white/10 text-white transition-all min-h-[44px] min-w-[44px]"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                {/* step progress */}
                <div className="relative px-6 md:px-8 pt-4">
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                        <div
                            className="h-full bg-amber-500 transition-all duration-500"
                            style={{ width: `${(step / 3) * 100}%` }}
                        ></div>
                    </div>
                </div>

                <div className="relative flex-1 overflow-y-auto p-6 md:p-8 space-y-5">
                    {step === 1 && (
                        <>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                                    Tipo de cadastro
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { id: "PF", label: "Pessoa Física", desc: "CPF", icon: "fa-user" },
                                        { id: "PJ", label: "Pessoa Jurídica", desc: "CNPJ", icon: "fa-building" },
                                    ].map((k) => {
                                        const active = kind === k.id;
                                        return (
                                            <button
                                                key={k.id}
                                                data-testid={`register-kind-${k.id}`}
                                                onClick={() => {
                                                    setKind(k.id);
                                                    setDoc("");
                                                }}
                                                className={`min-h-[64px] flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                                                    active
                                                        ? "border-amber-500/60 bg-amber-500/5"
                                                        : "border-white/10 hover:border-white/30 bg-slate-900/40"
                                                }`}
                                            >
                                                <i className={`fa-solid ${k.icon} ${active ? "text-amber-400" : "text-slate-400"}`}></i>
                                                <div className="text-left">
                                                    <div className="text-sm font-semibold text-white">{k.label}</div>
                                                    <div className="text-[11px] text-slate-500">{k.desc}</div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <Field label={kind === "PF" ? "CPF" : "CNPJ"} testId="register-document">
                                <input
                                    value={doc}
                                    onChange={(e) =>
                                        setDoc(kind === "PF" ? maskCPF(e.target.value) : maskCNPJ(e.target.value))
                                    }
                                    placeholder={kind === "PF" ? "000.000.000-00" : "00.000.000/0000-00"}
                                    className={inputCls}
                                    data-testid="register-document-input"
                                />
                            </Field>

                            <Field label="Nome completo" testId="register-owner-name">
                                <input
                                    value={ownerName}
                                    onChange={(e) => setOwnerName(e.target.value)}
                                    placeholder="Como você se chama?"
                                    className={inputCls}
                                    data-testid="register-owner-name-input"
                                />
                            </Field>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label="E-mail" testId="register-email">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="voce@email.com"
                                        className={inputCls}
                                        data-testid="register-email-input"
                                    />
                                </Field>
                                <Field label="Telefone / WhatsApp" testId="register-phone">
                                    <input
                                        value={phone}
                                        onChange={(e) => setPhone(maskPhone(e.target.value))}
                                        placeholder="(00) 00000-0000"
                                        className={inputCls}
                                        data-testid="register-phone-input"
                                    />
                                </Field>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <Field label="Nome fantasia do salão" testId="register-shop-name">
                                <input
                                    value={shopName}
                                    onChange={(e) => {
                                        setShopName(e.target.value);
                                        if (!shopId) setShopId(slugifyShopName(e.target.value));
                                    }}
                                    placeholder="Ex: Vintage Club Barbearia"
                                    className={inputCls}
                                    data-testid="register-shop-name-input"
                                />
                            </Field>

                            <Field
                                label="Identificador único (URL pública)"
                                testId="register-shop-id"
                                hint="Apenas letras minúsculas e números"
                            >
                                <div className="flex items-center rounded-xl border border-white/10 bg-slate-950/60 focus-within:border-amber-500/60 transition-colors">
                                    <span className="px-3 text-slate-500 text-sm font-mono select-none">
                                        kinng.app/
                                    </span>
                                    <input
                                        value={shopId}
                                        onChange={(e) => setShopId(slugifyShopName(e.target.value))}
                                        placeholder="meusalao"
                                        className="flex-1 bg-transparent py-3 pr-4 text-white outline-none placeholder:text-slate-600 font-mono text-sm min-h-[44px]"
                                        data-testid="register-shop-id-input"
                                    />
                                </div>
                            </Field>

                            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-amber-200/80 leading-relaxed">
                                <i className="fa-solid fa-circle-info mr-2 text-amber-400"></i>
                                Sua URL pública ficará disponível em <span className="font-mono text-amber-300">
                                kinng.app/{shopId || "seu-id"}</span>. Você pode conectar um domínio próprio
                                depois pelo Estúdio de Marca.
                            </div>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2">
                                    Escolha um plano
                                </div>
                                <div className="grid md:grid-cols-3 gap-3">
                                    {PLANS.map((p) => {
                                        const active = plan === p.id;
                                        return (
                                            <button
                                                key={p.id}
                                                data-testid={`register-plan-${p.id}`}
                                                onClick={() => setPlan(p.id)}
                                                className={`relative text-left rounded-xl border p-4 transition-all min-h-[140px] ${
                                                    active
                                                        ? "border-amber-500/60 bg-amber-500/5"
                                                        : "border-white/10 hover:border-white/30 bg-slate-900/40"
                                                }`}
                                            >
                                                {p.recommended && (
                                                    <span className="absolute -top-2 right-2 text-[9px] uppercase tracking-wider bg-amber-500 text-slate-950 px-2 py-0.5 rounded-full font-bold">
                                                        Recomendado
                                                    </span>
                                                )}
                                                <div className="salon-heading text-white text-lg">{p.id}</div>
                                                <div className="text-xs text-slate-400 mt-0.5 font-mono">
                                                    R$ {p.price.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mês
                                                </div>
                                                <ul className="mt-3 space-y-1">
                                                    {p.perks.map((perk) => (
                                                        <li key={perk} className="text-[11px] text-slate-300 flex items-start gap-1.5">
                                                            <i className="fa-solid fa-check text-emerald-400 text-[9px] mt-0.5"></i>
                                                            {perk}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Senha (8+ chars)" testId="register-password">
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className={passwordInputCls}
                                            data-testid="register-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((v) => !v)}
                                            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                            className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                            data-testid="register-password-toggle"
                                        >
                                            <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                                        </button>
                                    </div>
                                </Field>
                                <Field label="Confirmar senha" testId="register-confirm">
                                    <div className="relative">
                                        <input
                                            type={showConfirm ? "text" : "password"}
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            placeholder="••••••••"
                                            className={passwordInputCls}
                                            data-testid="register-confirm-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirm((v) => !v)}
                                            aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
                                            className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                            data-testid="register-confirm-toggle"
                                        >
                                            <i className={`fa-solid ${showConfirm ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                                        </button>
                                    </div>
                                </Field>
                            </div>

                            <label className="flex items-start gap-3 text-xs text-slate-400 mt-2">
                                <span className="mt-0.5 h-4 w-4 rounded border border-amber-500/50 bg-amber-500/20 flex items-center justify-center shrink-0">
                                    <i className="fa-solid fa-check text-amber-400 text-[9px]"></i>
                                </span>
                                <span>
                                    Aceito os <span className="text-amber-400 underline">Termos de Uso</span> e a{" "}
                                    <span className="text-amber-400 underline">Política de Privacidade</span> da
                                    plataforma Kinng.app.
                                </span>
                            </label>

                            {/* PINs de segurança operacional */}
                            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 mt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <i className="fa-solid fa-lock text-amber-400"></i>
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-semibold">
                                        PINs Operacionais (4 dígitos)
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="PIN Modo Kiosk" testId="register-kiosk-pin" hint="Trava a saída do PDV">
                                        <input
                                            inputMode="numeric"
                                            maxLength={4}
                                            value={kioskPin}
                                            onChange={(e) => setKioskPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                            placeholder="0000"
                                            className={`${inputCls} font-mono text-center text-lg tracking-[0.5em]`}
                                            data-testid="register-kiosk-pin-input"
                                        />
                                    </Field>
                                    <Field label="PIN Painel Financeiro" testId="register-fin-pin" hint="Acesso aos relatórios">
                                        <input
                                            inputMode="numeric"
                                            maxLength={4}
                                            value={financialPin}
                                            onChange={(e) => setFinancialPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                            placeholder="0000"
                                            className={`${inputCls} font-mono text-center text-lg tracking-[0.5em]`}
                                            data-testid="register-fin-pin-input"
                                        />
                                    </Field>
                                </div>
                                <p className="text-[11px] text-amber-200/70 mt-3 leading-relaxed">
                                    <i className="fa-solid fa-circle-info mr-1"></i>
                                    Esses PINs podem ser alterados a qualquer momento no <strong>Estúdio de Marca</strong>.
                                </p>
                            </div>
                        </>
                    )}
                </div>

                <footer className="relative p-6 border-t border-white/5 flex items-center justify-between gap-3">
                    {step > 1 ? (
                        <button
                            data-testid="register-back-btn"
                            onClick={() => setStep((s) => s - 1)}
                            className="min-h-[48px] px-5 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 hover:text-white text-sm transition-all"
                        >
                            <i className="fa-solid fa-arrow-left mr-2"></i>Voltar
                        </button>
                    ) : (
                        <span className="text-xs text-slate-500">
                            Já tem uma conta? <button onClick={onClose} className="text-amber-400 hover:underline">Entrar</button>
                        </span>
                    )}
                    {step < 3 ? (
                        <button
                            data-testid="register-next-btn"
                            onClick={handleNext}
                            className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-wider text-sm transition-all shadow-lg shadow-amber-500/20"
                        >
                            Continuar <i className="fa-solid fa-arrow-right ml-2"></i>
                        </button>
                    ) : (
                        <button
                            data-testid="register-submit-btn"
                            onClick={handleSubmit}
                            disabled={loading}
                            className="min-h-[48px] px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-wider text-sm transition-all shadow-lg shadow-amber-500/20 disabled:opacity-70"
                        >
                            {loading ? (
                                <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Criando conta…</>
                            ) : (
                                <>Finalizar cadastro <i className="fa-solid fa-rocket ml-2"></i></>
                            )}
                        </button>
                    )}
                </footer>
            </div>
        </div>
    );
};

const inputCls =
    "w-full bg-slate-950/60 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-500/60 transition-colors min-h-[48px]";
// Igual a inputCls, com espaço reservado à direita pro botão de mostrar/ocultar senha.
const passwordInputCls =
    "w-full bg-slate-950/60 border border-white/10 rounded-xl pl-4 pr-11 py-3 text-white text-sm outline-none focus:border-amber-500/60 transition-colors min-h-[48px]";

const Field = ({ label, testId, hint, children }) => (
    <label className="block" data-testid={testId}>
        <span className="text-[11px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
        <div className="mt-1.5">{children}</div>
        {hint && <span className="text-[11px] text-slate-500 mt-1 block">{hint}</span>}
    </label>
);

export default RegisterModal;
